import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Spinner, Alert, Image,
  Badge, Button, ListGroup, ProgressBar, Tabs, Tab
} from 'react-bootstrap';
import { 
  FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaYoutube, 
  FaWhatsapp, FaEnvelope, FaPhone, FaBriefcase, FaGraduationCap, 
  FaQuoteLeft, FaAward, FaLightbulb, FaHandshake, FaPaperPlane, 
  FaCalendarCheck, FaUserCircle, FaMapMarkerAlt, FaGlobe, 
  FaBirthdayCake, FaStar, FaUsers, FaChartLine, FaBuilding,
  FaRocket, FaShieldAlt, FaCode, FaDatabase
} from 'react-icons/fa';
import axios from 'axios';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const OwnerProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const API_BASE_URL = BASE_URL;

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${API_BASE_URL}/owner_profiles.php`);
      
      if (response.data.success && response.data.data) {
        setProfile(response.data.data);
      } else {
        setProfile(null);
      }
    } catch (err) {
      setError('Error loading owner profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${UPLOAD_BASE_URL}${imagePath}`;
  };

  const getSocialIcon = (platform) => {
    switch(platform) {
      case 'facebook': return <FaFacebook />;
      case 'twitter': return <FaTwitter />;
      case 'instagram': return <FaInstagram />;
      case 'linkedin': return <FaLinkedin />;
      case 'youtube': return <FaYoutube />;
      case 'whatsapp': return <FaWhatsapp />;
      default: return null;
    }
  };

  const getSocialColor = (platform) => {
    switch(platform) {
      case 'facebook': return '#1877f2';
      case 'twitter': return '#1da1f2';
      case 'instagram': return '#e4405f';
      case 'linkedin': return '#0a66c2';
      case 'youtube': return '#ff0000';
      case 'whatsapp': return '#25d366';
      default: return '#6c757d';
    }
  };

  const getWhatsAppLink = (phone) => {
    if (!phone) return '#';
    const cleanPhone = phone.replace(/\D/g, '');
    return `https://wa.me/${cleanPhone}?text=Hello%20I%20would%20like%20to%20connect%20with%20you`;
  };

  // Extract years of experience from JSON data
  const getExperienceYears = () => {
    if (!profile || !profile.experience) return '15+ Years Experience';
    
    // Try to parse years from experience field
    const experience = profile.experience;
    const yearMatch = experience.match(/(\d+)\+?\s*(year|yr|years)/i);
    
    if (yearMatch) {
      return `${yearMatch[1]}+ Years Experience`;
    }
    
    // Fallback to display as-is
    return experience;
  };

  const socialPlatforms = ['facebook', 'twitter', 'instagram', 'linkedin', 'youtube'];

  // Calculate profile completeness
  const calculateProfileCompleteness = () => {
    if (!profile) return 0;
    const fields = ['full_name', 'bio', 'email', 'phone', 'profile_image', 
                   'experience', 'qualifications', 'facebook', 'linkedin'];
    const filledFields = fields.filter(field => profile[field] && profile[field].trim()).length;
    return Math.round((filledFields / fields.length) * 100);
  };

  // Professional skills data
  const professionalSkills = [
    { name: 'Strategic Planning', icon: <FaChartLine />, level: 95 },
    { name: 'Leadership', icon: <FaUsers />, level: 92 },
    { name: 'Business Development', icon: <FaBuilding />, level: 88 },
    { name: 'Innovation', icon: <FaRocket />, level: 90 },
    { name: 'Risk Management', icon: <FaShieldAlt />, level: 85 },
    { name: 'Technical Expertise', icon: <FaCode />, level: 87 }
  ];

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <div className="spinner-container">
            <Spinner animation="border" variant="primary" />
            <Spinner animation="border" variant="success" className="ms-2" />
            <Spinner animation="border" variant="warning" className="ms-2" />
          </div>
          <h4 className="mt-4 text-gradient">Loading Professional Profile</h4>
          <p className="text-muted">Retrieving executive information...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="border-0 shadow-lg">
          <div className="d-flex align-items-center">
            <div className="flex-shrink-0">
              <FaUserCircle size={40} className="text-danger" />
            </div>
            <div className="ms-3">
              <Alert.Heading>Profile Loading Error</Alert.Heading>
              <p>{error}</p>
              <Button variant="outline-danger" onClick={fetchProfile}>
                Retry Loading
              </Button>
            </div>
          </div>
        </Alert>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container className="py-5">
        <div className="empty-state text-center">
          <div className="empty-icon mb-4">
            <FaUserCircle size={80} className="text-muted" />
          </div>
          <h3>Executive Profile Unavailable</h3>
          <p className="text-muted mb-4">Owner profile information is not currently available.</p>
          <Button variant="primary" onClick={fetchProfile}>
            Check Again
          </Button>
        </div>
      </Container>
    );
  }

  const profileCompleteness = calculateProfileCompleteness();
  const experienceYears = getExperienceYears();

  return (
    <div className="owner-profile-executive">
      {/* Subtle Background Pattern */}
      <div className="background-pattern"></div>

      <Container className="py-4 py-lg-5">
        {/* Executive Profile Header */}
        <Card className="border-0 shadow-lg mb-4 profile-executive-header">
          <Card.Body className="p-4 p-lg-5">
            <Row className="align-items-center">
              <Col lg={3} className="mb-4 mb-lg-0">
                <div className="executive-photo-wrapper">
                  {profile.profile_image ? (
                    <div className="executive-photo-container">
                      <Image
                        src={getImageUrl(profile.profile_image)}
                        alt={profile.full_name}
                        className="executive-photo"
                        fluid
                      />
                      <div className="executive-badge">
                        <FaStar className="text-warning" />
                      </div>
                    </div>
                  ) : (
                    <div className="executive-photo-placeholder">
                      <FaUserCircle size={60} />
                    </div>
                  )}
                </div>
              </Col>
              
              <Col lg={6} className="mb-4 mb-lg-0">
                <div className="executive-info">
                  <div className="d-flex align-items-center mb-3">
                    <h1 className="h2 mb-0 me-3">{profile.full_name}</h1>
                    <Badge bg="primary" className="py-2 px-3 badge-executive">
                      <FaStar className="me-1" /> Executive
                    </Badge>
                  </div>
                  
                  <div className="executive-title mb-4">
                    <h4 className="text-muted fw-normal">
                      <FaBriefcase className="me-2" />
                      {profile.professional_roles || 'Founder & Chief Executive Officer'}
                    </h4>
                  </div>
                  
                  <div className="executive-highlights">
                    <div className="d-flex flex-wrap gap-3 mb-3">
                      {profile.experience && (
                        <div className="highlight-item">
                          <FaChartLine className="text-primary me-2" />
                          <span className="fw-medium">{experienceYears}</span>
                        </div>
                      )}
                      {profile.qualifications && (
                        <div className="highlight-item">
                          <FaGraduationCap className="text-success me-2" />
                          <span className="fw-medium">{profile.qualifications}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
              
              <Col lg={3}>
                <div className="executive-stats">
                  <div className="stat-card">
                    <div className="stat-icon bg-primary-gradient">
                      <FaUsers />
                    </div>
                    <div className="stat-details">
                      <h3 className="mb-0">500+</h3>
                      <small>Professional Network</small>
                    </div>
                  </div>
                  
                  <div className="stat-card mt-3">
                    <div className="stat-icon bg-success-gradient">
                      <FaHandshake />
                    </div>
                    <div className="stat-details">
                      <h3 className="mb-0">10+</h3>
                      <small>Successful Projects</small>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            
            {/* Profile Completeness */}
            <div className="mt-4 pt-4 border-top">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted">Profile Strength</span>
                <span className="fw-bold">{profileCompleteness}%</span>
              </div>
              <ProgressBar 
                now={profileCompleteness} 
                className="profile-strength-bar"
                variant={profileCompleteness > 80 ? "success" : profileCompleteness > 50 ? "warning" : "danger"}
              />
            </div>
          </Card.Body>
        </Card>

        <Row className="g-4">
          {/* Main Content */}
          <Col lg={8}>
            {/* Executive Tabs */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-0">
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="executive-tabs"
                >
                  <Tab eventKey="overview" title="Executive Summary">
                    <div className="p-4 p-lg-5">
                      <div className="d-flex align-items-center mb-4">
                        <div className="tab-icon me-3">
                          <FaUserCircle size={24} className="text-primary" />
                        </div>
                        <h4 className="mb-0">Professional Overview</h4>
                      </div>
                      
                      {profile.bio ? (
                        <div className="executive-bio">
                          {profile.bio.split('\n').map((paragraph, index) => (
                            <div key={index} className="bio-paragraph mb-4">
                              <p className="lead text-muted">
                                {paragraph}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Alert variant="light" className="border-start border-primary border-3">
                          <FaUserCircle className="me-2 text-primary" />
                          Executive biography information is being updated.
                        </Alert>
                      )}
                    </div>
                  </Tab>
                  
                  <Tab eventKey="expertise" title="Core Competencies">
                    <div className="p-4 p-lg-5">
                      <div className="d-flex align-items-center mb-4">
                        <div className="tab-icon me-3">
                          <FaChartLine size={24} className="text-primary" />
                        </div>
                        <h4 className="mb-0">Professional Expertise</h4>
                      </div>
                      
                      <div className="competencies-grid">
                        {professionalSkills.map((skill, index) => (
                          <div key={index} className="competency-item">
                            <div className="competency-header">
                              <div className="competency-icon">
                                {skill.icon}
                              </div>
                              <div className="competency-name">
                                <h6 className="mb-0">{skill.name}</h6>
                              </div>
                              <div className="competency-level">
                                <span className="badge bg-light text-dark">{skill.level}%</span>
                              </div>
                            </div>
                            <ProgressBar 
                              now={skill.level} 
                              className="competency-progress"
                              variant="primary"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Tab>
                </Tabs>
              </Card.Body>
            </Card>

            {/* Executive Philosophy */}
            {/* <Card className="border-0 shadow-sm">
              <Card.Body className="p-4 p-lg-5 bg-light">
                <div className="d-flex align-items-start">
                  <div className="flex-shrink-0">
                    <FaQuoteLeft size={32} className="text-primary opacity-50 me-4" />
                  </div>
                  <div>
                    <h5 className="mb-3">Leadership Philosophy</h5>
                    <p className="lead fst-italic text-muted mb-3">
                      "True leadership is not about being in charge, but about taking care of those in your charge. 
                      It's about empowering teams, fostering innovation, and driving sustainable growth through 
                      strategic vision and operational excellence."
                    </p>
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <div className="signature-line"></div>
                      </div>
                      <div className="ms-3">
                        <strong className="d-block">{profile.full_name}</strong>
                        <small className="text-muted">Chief Executive Officer</small>
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card> */}
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            {/* Contact Information */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-4">
                  <div className="sidebar-icon me-3">
                    <FaPhone size={20} className="text-primary" />
                  </div>
                  <h5 className="mb-0">Contact Information</h5>
                </div>
                
                <div className="contact-list">
                  {profile.email && (
                    <div className="contact-item mb-3">
                      <div className="contact-icon email">
                        <FaEnvelope />
                      </div>
                      <div className="contact-details">
                        <small className="text-muted d-block">Email Address</small>
                        <a href={`mailto:${profile.email}`} className="text-decoration-none">
                          {profile.email}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {profile.phone && (
                    <div className="contact-item mb-4">
                      <div className="contact-icon phone">
                        <FaPhone />
                      </div>
                      <div className="contact-details">
                        <small className="text-muted d-block">Direct Line</small>
                        <a href={`tel:${profile.phone}`} className="text-decoration-none">
                          {profile.phone}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="contact-actions">
                  {profile.email && (
                    <Button variant="outline-primary" className="w-100 mb-2" href={`mailto:${profile.email}`}>
                      <FaEnvelope className="me-2" /> Send Email
                    </Button>
                  )}
                  
                  {profile.phone && (
                    <Button 
                      variant="outline-success" 
                      className="w-100"
                      href={getWhatsAppLink(profile.phone)}
                      target="_blank"
                    >
                      <FaWhatsapp className="me-2" /> WhatsApp Message
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>

            {/* Professional Network */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-4">
                  <div className="sidebar-icon me-3">
                    <FaLinkedin size={20} className="text-primary" />
                  </div>
                  <h5 className="mb-0">Professional Network</h5>
                </div>
                
                <div className="network-grid">
                  {socialPlatforms.map(platform => (
                    profile[platform] && (
                      <a
                        key={platform}
                        href={profile[platform]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="network-item"
                        style={{ '--social-color': getSocialColor(platform) }}
                        title={platform.charAt(0).toUpperCase() + platform.slice(1)}
                      >
                        <div className="network-icon">
                          {getSocialIcon(platform)}
                        </div>
                        <span className="network-label">
                          {platform.charAt(0).toUpperCase() + platform.slice(1)}
                        </span>
                      </a>
                    )
                  ))}
                  
                  {profile.phone && (
                    <a
                      href={getWhatsAppLink(profile.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="network-item"
                      style={{ '--social-color': getSocialColor('whatsapp') }}
                      title="WhatsApp"
                    >
                      <div className="network-icon">
                        <FaWhatsapp />
                      </div>
                      <span className="network-label">WhatsApp</span>
                    </a>
                  )}
                </div>
              </Card.Body>
            </Card>

            {/* Availability Status */}
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="sidebar-icon me-3">
                    <FaCalendarCheck size={20} className="text-primary" />
                  </div>
                  <h5 className="mb-0">Availability Status</h5>
                </div>
                
                <div className="availability-status">
                  <div className="status-indicator">
                    <div className="status-dot available"></div>
                    <span className="ms-2">Available for meetings</span>
                  </div>
                  
                  <ListGroup variant="flush" className="mt-3">
                    <ListGroup.Item className="d-flex justify-content-between align-items-center border-0 px-0 py-2">
                      <span className="text-muted">Response Time</span>
                      <Badge bg="success">Within 24 hours</Badge>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between align-items-center border-0 px-0 py-2">
                      <span className="text-muted">Meeting Duration</span>
                      <Badge bg="info">30-45 minutes</Badge>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between align-items-center border-0 px-0 py-2">
                      <span className="text-muted">Preferred Time</span>
                      <Badge bg="warning">Business Hours</Badge>
                    </ListGroup.Item>
                  </ListGroup>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <style >{`
        .owner-profile-executive {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          min-height: 100vh;
          position: relative;
        }
        
        .background-pattern {
          position: absolute;
          width: 100%;
          height: 100%;
          background-image: 
            radial-gradient(#cbd5e1 1px, transparent 1px),
            radial-gradient(#cbd5e1 1px, transparent 1px);
          background-size: 40px 40px;
          background-position: 0 0, 20px 20px;
          opacity: 0.3;
          pointer-events: none;
        }
        
        .profile-executive-header {
          background: white;
          border-radius: 16px;
          position: relative;
          overflow: hidden;
        }
        
        .profile-executive-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6, #10b981);
        }
        
        .executive-photo-wrapper {
          position: relative;
          display: inline-block;
        }
        
        .executive-photo-container {
          position: relative;
          width: 200px;
          height: 200px;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
        }
        
        .executive-photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .executive-photo-placeholder {
          width: 200px;
          height: 200px;
          border-radius: 12px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 60px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
        }
        
        .executive-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: white;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        .badge-executive {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          border: none;
        }
        
        .highlight-item {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          background: #f1f5f9;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }
        
        .executive-stats {
          display: flex;
          flex-direction: column;
        }
        
        .stat-card {
          display: flex;
          align-items: center;
          padding: 16px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          transition: all 0.3s ease;
        }
        
        .stat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }
        
        .stat-icon {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-right: 15px;
          font-size: 20px;
        }
        
        .bg-primary-gradient {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
        }
        
        .bg-success-gradient {
          background: linear-gradient(135deg, #10b981, #059669);
        }
        
        .profile-strength-bar {
          height: 6px;
          border-radius: 3px;
        }
        
        .profile-strength-bar .progress-bar {
          border-radius: 3px;
        }
        
        .executive-tabs .nav-link {
          border: none;
          color: #64748b;
          padding: 1rem 1.5rem;
          font-weight: 500;
          transition: all 0.3s;
          border-bottom: 2px solid transparent;
        }
        
        .executive-tabs .nav-link.active {
          color: #3b82f6;
          border-bottom: 2px solid #3b82f6;
          background: transparent;
        }
        
        .tab-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: #eff6ff;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .competencies-grid {
          display: grid;
          gap: 20px;
        }
        
        .competency-item {
          background: #f8fafc;
          padding: 16px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
        }
        
        .competency-header {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
        }
        
        .competency-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: #eff6ff;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #3b82f6;
          margin-right: 12px;
        }
        
        .competency-name {
          flex: 1;
        }
        
        .competency-progress {
          height: 6px;
          border-radius: 3px;
        }
        
        .competency-progress .progress-bar {
          border-radius: 3px;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
        }
        
        .signature-line {
          width: 60px;
          height: 2px;
          background: #3b82f6;
          opacity: 0.5;
        }
        
        .sidebar-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: #eff6ff;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .contact-list {
          margin-bottom: 24px;
        }
        
        .contact-item {
          display: flex;
          align-items: center;
          padding: 12px;
          background: #f8fafc;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          transition: all 0.3s ease;
        }
        
        .contact-item:hover {
          background: #f1f5f9;
          transform: translateX(5px);
        }
        
        .contact-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-right: 15px;
        }
        
        .contact-icon.email {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
        }
        
        .contact-icon.phone {
          background: linear-gradient(135deg, #10b981, #059669);
        }
        
        .network-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        
        .network-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 16px 8px;
          background: var(--social-color, #64748b);
          color: white;
          border-radius: 10px;
          text-decoration: none;
          transition: all 0.3s ease;
        }
        
        .network-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
          color: white;
          text-decoration: none;
        }
        
        .network-icon {
          font-size: 24px;
          margin-bottom: 8px;
        }
        
        .network-label {
          font-size: 11px;
          font-weight: 600;
          text-align: center;
          opacity: 0.9;
        }
        
        .availability-status {
          background: #f8fafc;
          padding: 16px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
        }
        
        .status-indicator {
          display: flex;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .status-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }
        
        .status-dot.available {
          background: #10b981;
          box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.2);
        }
        
        .text-gradient {
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        @media (max-width: 992px) {
          .executive-photo-container,
          .executive-photo-placeholder {
            width: 180px;
            height: 180px;
          }
          
          .network-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        @media (max-width: 768px) {
          .profile-executive-header .row {
            flex-direction: column;
          }
          
          .executive-photo-wrapper {
            margin-bottom: 24px;
            text-align: center;
          }
          
          .executive-photo-container,
          .executive-photo-placeholder {
            width: 150px;
            height: 150px;
          }
          
          .executive-info {
            text-align: center;
          }
          
          .executive-stats {
            margin-top: 24px;
          }
          
          .network-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        
        @media (max-width: 576px) {
          .executive-photo-container,
          .executive-photo-placeholder {
            width: 120px;
            height: 120px;
          }
          
          .network-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .contact-item {
            flex-direction: column;
            text-align: center;
          }
          
          .contact-icon {
            margin-right: 0;
            margin-bottom: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default OwnerProfile;